import { areasFit } from "./areas.ts";
import { actorInProject } from "./projects.ts";
import { anyOverlap, matchGlob } from "./glob.ts";
import { assessRisk } from "./risk.ts";
import { success, type OutcomeRow } from "./outcomes.ts";
import { evaluateRules } from "./rules.ts";
import { serviceCapability } from "./services.ts";
import { DEFAULT_TAXONOMY, inferSkills, type SkillDef } from "./skills.ts";
import { isArchivedMember } from "./roster.ts";
import { isChief, type Actor, type Mode, type RiskAssessment, type RoutableTask, type Rule } from "./types.ts";
import type { CodebaseMap } from "./workers/types.ts";

export type ActorLoad = {
  actorId: string;
  /** Tasks this actor builds that are in progress, verifying or in review. */
  activeTasks: number;
  /** Open review decisions assigned to this actor. Used only as a soft reviewer-ranking signal. */
  openReviews: number;
};

export type HistoryItem = { builderId: string; projectId: string; areas: string[] };

export type RouteContext = {
  actors: readonly Actor[];
  loads: readonly ActorLoad[];
  rules: readonly Rule[];
  history?: readonly HistoryItem[];
  taxonomy?: SkillDef[];
  /** Areas with an active spot-check penalty in this project. */
  penalizedAreas?: readonly string[];
  /** Finished build attempts in this project (landed or released), for the bounded learned term. Absent or empty: no effect. */
  outcomes?: readonly OutcomeRow[];
  /**
   * The project's codebase map, set only when the map worker is enabled. Continuity then comes from map ownership
   * (history is ignored), and a task covering a hot file gets an explanation reason. Hot files change no score.
   */
  map?: CodebaseMap;
};

export type TrackBasis = "areas+kind" | "areas";

/** An agent's record on tasks like this one (learned-routing spec §4.3). */
export type Track = {
  basis: TrackBasis;
  attempts: number;
  /** Landed and not reverted. */
  landed: number;
  firstTry: number;
  reverted: number;
  adjustment: number;
  /** Most attempts first; attempts without a run model are omitted. */
  byModel: { model: string; attempts: number; landed: number; firstTry: number }[];
};

export type ScoreBreakdown = {
  actorId: string;
  name: string;
  kind: Actor["kind"];
  score: number;
  skillMatch: number;
  /** Agent maxParallel availability. Null for people, who have no availability score. */
  availability: number | null;
  continuity: number;
  missingSkills: string[];
  /** Null for people, agents that fail clearsAgentSkillGate, and fewer than LEARNED_MIN_ATTEMPTS similar attempts. */
  track: Track | null;
};

export type ReviewerScore = {
  actorId: string;
  name: string;
  score: number;
  skillMatch: number;
  focusMatch: number;
};

export type Disqualification = { actorId: string; name: string; reason: string };

export type LearnedRouting = {
  builderId: string;
  basis: TrackBasis;
  attempts: number;
  landed: number;
  firstTry: number;
  reverted: number;
  adjustment: number;
  /** The builder differs from the baseline's top agent. */
  flipped: boolean;
  /** The baseline's top agent when flipped; attempts/landed are 0 when it has no track. */
  over: { actorId: string; name: string; attempts: number; landed: number } | null;
};

export type RouteSuggestion = {
  mode: Mode;
  builderId: string | null;
  reviewerIds: string[];
  skills: string[];
  risk: RiskAssessment;
  firedRules: { id: string; name: string; effect: Rule["effect"]; value: string | null }[];
  builders: ScoreBreakdown[];
  reviewers: ReviewerScore[];
  disqualified: Disqualification[];
  explanation: string;
  confidence: number;
  /** What history the router saw and whether it changed the pick; null when the builder is not an agent with a track. */
  learned: LearnedRouting | null;
};

/** The minimum skillMatch for routing to pick an agent at all; exported so other matchers (e.g. task-areas-fit-agents' "is there a real candidate") don't duplicate the literal. */
export const AGENT_SKILL_THRESHOLD = 0.4;

/**
 * The skill gate an agent must pass to build a task (routeTask's clearsSkill, the learned track and the
 * server's area-fit split hint all use this one predicate). Explicit skills must reach AGENT_SKILL_THRESHOLD.
 * Inferred skills only need any match above 0: they include keyword hits, so the floor is deliberately weak (#1516).
 * The match is rounded as routing rounds scores, so borderline values agree everywhere.
 */
export function clearsAgentSkillGate(explicit: boolean, match: number): boolean {
  const m = round(match);
  return explicit ? m >= AGENT_SKILL_THRESHOLD : m > 0;
}

/** Disqualification reason when a fence doesn't cover the task's areas; exported so other matchers can compare against it without duplicating the literal. */
export const AREAS_DISQUALIFIED = "task areas are outside this agent's allowed areas";

/** Disqualification reason for an agent assigned only to other projects (projects spec §4.3); exported so other matchers can compare against it. */
export const NOT_ASSIGNED_TO_PROJECT = "agent is not assigned to this project";

/** `reason` on the `task.stage_changed` event that parks a task whose areas fit no single agent (dec_vvhs5hfjx6): blocked, and unblocked by splitting it. */
export const NEEDS_SPLIT = "needs_split";

/** Similar attempts needed before an agent's track has any effect. */
export const LEARNED_MIN_ATTEMPTS = 3;
/** Only this many of the most recent similar attempts count. */
export const LEARNED_WINDOW = 20;

function trackOf(
  outcomes: readonly OutcomeRow[],
  actorId: string,
  task: Pick<RoutableTask, "projectId" | "kind" | "areas">,
): { track: Track; raw: number } | null {
  const mine = outcomes.filter(
    (o) => o.builderId === actorId && o.projectId === task.projectId && (o.landed || o.released) && anyOverlap(o.areas, task.areas),
  );
  const sameKind = mine.filter((o) => o.kind === task.kind);
  const byKind = sameKind.length >= LEARNED_MIN_ATTEMPTS;
  const rows = (byKind ? sameKind : mine).sort((a, b) => b.startedAt - a.startedAt).slice(0, LEARNED_WINDOW);
  const n = rows.length;
  if (n < LEARNED_MIN_ATTEMPTS) return null;
  const rate = (rows.reduce((sum, r) => sum + success(r), 0) + 1) / (n + 2);
  const raw = 0.1 * (rate - 0.5) * Math.min(1, n / 5);
  const count = (rs: readonly OutcomeRow[]) => ({
    attempts: rs.length,
    landed: rs.filter((r) => r.landed && !r.reverted).length,
    firstTry: rs.filter((r) => success(r) === 1).length,
  });
  const models = new Map<string, OutcomeRow[]>();
  for (const r of rows) if (r.model) models.set(r.model, [...(models.get(r.model) ?? []), r]);
  return {
    raw,
    track: {
      basis: byKind ? "areas+kind" : "areas",
      ...count(rows),
      reverted: rows.filter((r) => r.reverted).length,
      // Three decimals: two would round the largest possible adjustment (0.0455) up to the 0.05 bound.
      adjustment: Math.round(raw * 1000) / 1000,
      byModel: [...models]
        .map(([model, rs]) => ({ model, ...count(rs) }))
        .sort((a, b) => b.attempts - a.attempts || a.model.localeCompare(b.model)),
    },
  };
}

function round(n: number): number {
  return Math.round(n * 100) / 100;
}

/** Public builder score before the learned adjustment. Humans have no availability component. */
export function baseBuilderScore(kind: Actor["kind"], skillMatch: number, availability: number | null, continuity: number): number {
  return kind === "human" ? 0.8 * skillMatch + 0.2 * continuity : 0.6 * skillMatch + 0.25 * (availability ?? 0) + 0.15 * continuity;
}

/** Public reviewer score; review queue length is only an equal-score tie-break. */
export function reviewerScore(skillMatch: number, focusMatch: number): number {
  return round(Math.max(0, 0.9 * skillMatch + 0.1 * focusMatch));
}

/** Review-policy count used by live routing and persisted-suggestion repairs. */
export function requiredReviewerCount(mode: Mode, tier: RiskAssessment["tier"], requireHumanReview: boolean): number {
  if (tier === "critical") return 2;
  if (mode === "agent_with_review" || (mode === "human" && (tier !== "low" || requireHumanReview))) return 1;
  return 0;
}

/** How many of the most-churned files count as hot (background-workers spec, scope item 7). */
const HOT_FILE_LIMIT = 20;

/** A map area (`apps/server`, or already `apps/server/**`) as the directory glob it stands for. */
function dirGlob(area: string): string {
  return `${area.replace(/\/?\*\*$/, "").replace(/\/+$/, "")}/**`;
}

/**
 * Share of the task's areas that overlap an area the actor owns in the map, in [0, 1].
 * A map area `apps/server` is read as the glob `apps/server/**`, so a task area `apps/server/**` counts for its owners.
 */
function mapContinuity(map: CodebaseMap, actorId: string, areas: readonly string[]): number {
  if (areas.length === 0) return 0;
  const owned = map.areas.filter((a) => a.owners.some((o) => o.actorId === actorId)).map((a) => dirGlob(a.area));
  if (owned.length === 0) return 0;
  return round(areas.filter((area) => anyOverlap([area], owned)).length / areas.length);
}

export function skillMatch(actor: Pick<Actor, "skills">, skills: readonly string[]): { value: number; missing: string[] } {
  if (skills.length === 0) return { value: 0.5, missing: [] };
  let total = 0;
  const missing: string[] = [];
  for (const key of skills) {
    const level = actor.skills.find((s) => s.key === key)?.level ?? 0;
    if (level === 0) missing.push(key);
    total += Math.min(5, Math.max(0, level)) / 5;
  }
  return { value: total / skills.length, missing };
}

function loadOf(loads: readonly ActorLoad[], actorId: string): ActorLoad {
  return loads.find((l) => l.actorId === actorId) ?? { actorId, activeTasks: 0, openReviews: 0 };
}

// Human workload breaks equal public scores only; it is never a score, eligibility check or claim gate.

export function routeTask(task: RoutableTask, ctx: RouteContext): RouteSuggestion {
  // Skills a person set on the task are the ones routing uses; inference only runs when none were set (#1516).
  const explicit = task.skills.length > 0;
  const skills = explicit ? [...new Set(task.skills)].sort() : inferSkills(task, ctx.taxonomy ?? DEFAULT_TAXONOMY);
  const rules = evaluateRules(ctx.rules, task);
  const areaPenalty = (ctx.penalizedAreas ?? []).length > 0 && anyOverlap(task.areas, ctx.penalizedAreas ?? []);
  const risk = assessRisk(task, { ruleMinTier: rules.minTier, areaPenalty });
  const byId = new Map(ctx.actors.map((a) => [a.id, a]));

  const disqualified: Disqualification[] = [];
  const eligible: Actor[] = [];
  for (const actor of ctx.actors) {
    // Removed people are not candidates and are not listed as disqualified: the room no longer shows them.
    // Explicit role check: the Chief's empty allowedAreas would otherwise fit every task (areasFit).
    if (isArchivedMember(actor) || !actor.active || actor.access === "viewer" || isChief(actor)) continue;
    const load = loadOf(ctx.loads, actor.id);
    if (actor.kind === "human" && actor.canBuild === false) {
      disqualified.push({ actorId: actor.id, name: actor.name, reason: "person opted out of building" });
      continue;
    }
    if (actor.kind === "agent") {
      if (!actorInProject(actor, task.projectId)) {
        disqualified.push({ actorId: actor.id, name: actor.name, reason: NOT_ASSIGNED_TO_PROJECT });
        continue;
      }
      if (rules.forbidAgentBuilder) {
        disqualified.push({ actorId: actor.id, name: actor.name, reason: "rule forbids agent builders here" });
        continue;
      }
      if (!areasFit(actor.allowedAreas, task.areas)) {
        disqualified.push({ actorId: actor.id, name: actor.name, reason: AREAS_DISQUALIFIED });
        continue;
      }
      if (actor.maxParallel === 0) {
        disqualified.push({ actorId: actor.id, name: actor.name, reason: "review-only agent (no build slots)" });
        continue;
      }
      if (load.activeTasks >= actor.maxParallel) {
        disqualified.push({ actorId: actor.id, name: actor.name, reason: `at capacity (${load.activeTasks}/${actor.maxParallel})` });
        continue;
      }
    }
    eligible.push(actor);
  }

  type Candidate = ScoreBreakdown & { rawBase: number; activeTasks: number };
  const outcomes = ctx.outcomes ?? [];
  const candidates: Candidate[] = eligible.map((actor) => {
    const load = loadOf(ctx.loads, actor.id);
    const match = skillMatch(actor, skills);
    // Agents retain maxParallel because it is a machine/subscription concurrency setting. People
    // have no availability score; active work breaks ties between equal published scores only.
    const availability = actor.kind === "agent" ? Math.max(0, 1 - load.activeTasks / Math.max(1, actor.maxParallel)) : null;
    const continuity = ctx.map
      ? mapContinuity(ctx.map, actor.id, task.areas)
      : (ctx.history ?? []).some((h) => h.builderId === actor.id && h.projectId === task.projectId && anyOverlap(h.areas, task.areas))
        ? 1
        : 0;
    const rawBase = baseBuilderScore(actor.kind, match.value, availability, continuity);
    // Only agents that clear the skill gate learn; people never get a track (NG3).
    const learnt = actor.kind === "agent" && clearsAgentSkillGate(explicit, match.value) && outcomes.length > 0 ? trackOf(outcomes, actor.id, task) : null;
    const rawScore = Math.min(1, Math.max(0, rawBase + (learnt?.raw ?? 0)));
    return {
      actorId: actor.id,
      name: actor.name,
      kind: actor.kind,
      score: round(rawScore),
      skillMatch: round(match.value),
      availability: availability === null ? null : round(availability),
      continuity,
      missingSkills: match.missing,
      track: learnt?.track ?? null,
      rawBase,
      activeTasks: actor.kind === "human" ? load.activeTasks : 0,
    };
  });
  const byBaseline = (a: Candidate, b: Candidate) =>
    round(b.rawBase) - round(a.rawBase) || a.activeTasks - b.activeTasks || a.name.localeCompare(b.name);
  const byLearnedScore = (a: Candidate, b: Candidate) =>
    b.score - a.score || a.activeTasks - b.activeTasks || a.name.localeCompare(b.name);
  const published = ({ rawBase: _rawBase, activeTasks: _activeTasks, ...rest }: Candidate): ScoreBreakdown => rest;
  // The baseline is today's ranking (no learned term); it alone decides the mode.
  const baseline = [...candidates].sort(byBaseline);
  const rankedCandidates = [...candidates].sort(byLearnedScore);
  const builders = rankedCandidates.map(published);

  // Eligibility already means the agent may edit every area. The skill gate (clearsAgentSkillGate) is the only skill
  // check; an agent with no inferred skill at all still does not build (#1516).
  const clearsSkill = (b: ScoreBreakdown) => clearsAgentSkillGate(explicit, b.skillMatch);
  const bestAgent = baseline.find((b) => b.kind === "agent");
  const bestHuman = baseline.find((b) => b.kind === "human");
  // The learned score only picks among agents that clear the skill gate.
  const learnedAgent = builders.find((b) => b.kind === "agent" && clearsSkill(b));

  let mode: Mode;
  if (rules.forbidAgentBuilder || risk.tier === "critical" || !bestAgent || !clearsSkill(bestAgent)) {
    mode = "human";
  } else if (risk.tier === "low" && !rules.requireHumanReviewer) {
    mode = "agent";
  } else {
    mode = "agent_with_review";
  }

  const asScored = (c: Candidate | undefined) => (c ? builders.find((b) => b.actorId === c.actorId) : undefined);
  let builder: ScoreBreakdown | undefined = mode === "human" ? asScored(bestHuman) : learnedAgent;
  if (!builder) {
    // No human available for a human-mode task: fall back to the best agent only if agents are allowed.
    if (mode === "human" && bestAgent && !rules.forbidAgentBuilder && risk.tier !== "critical" && clearsSkill(bestAgent)) {
      mode = "agent_with_review";
      builder = learnedAgent;
    }
  }
  const builderActor = builder ? byId.get(builder.actorId) : undefined;

  // The flip is judged against the full candidate list, not the top-3 the suggestion keeps.
  let learned: LearnedRouting | null = null;
  if (builder && builder.kind === "agent" && bestAgent) {
    const flipped = builder.actorId !== bestAgent.actorId;
    if (builder.track || flipped) {
      const t = builder.track;
      learned = {
        builderId: builder.actorId,
        basis: t?.basis ?? "areas",
        attempts: t?.attempts ?? 0,
        landed: t?.landed ?? 0,
        firstTry: t?.firstTry ?? 0,
        reverted: t?.reverted ?? 0,
        adjustment: t?.adjustment ?? 0,
        flipped,
        over: flipped ? { actorId: bestAgent.actorId, name: bestAgent.name, attempts: bestAgent.track?.attempts ?? 0, landed: bestAgent.track?.landed ?? 0 } : null,
      };
    }
  }

  const reviewerCount = requiredReviewerCount(mode, risk.tier, rules.requireHumanReviewer);

  const excludeOwner = (risk.tier === "high" || risk.tier === "critical") && builderActor?.kind === "agent";
  const reviewerPool = ctx.actors.filter(
    (a) =>
      a.kind === "human" &&
      !isChief(a) &&
      a.active &&
      !isArchivedMember(a) &&
      a.access !== "viewer" &&
      a.id !== builder?.actorId &&
      !(excludeOwner && a.id === builderActor?.ownerId),
  );

  const reviewerCandidates = reviewerPool
    .map((actor) => {
      const focusMatch = rules.requiredFocus.length === 0 ? 0 : rules.requiredFocus.some((f) => actor.focus.includes(f)) ? 1 : 0;
      const match = skillMatch(actor, skills).value;
      const openReviews = loadOf(ctx.loads, actor.id).openReviews;
      const score = reviewerScore(match, focusMatch);
      return {
        actorId: actor.id,
        name: actor.name,
        score,
        skillMatch: round(match),
        focusMatch,
        openReviews,
      };
    })
    .sort((a, b) => b.score - a.score || a.openReviews - b.openReviews || a.name.localeCompare(b.name));
  const reviewers: ReviewerScore[] = reviewerCandidates.map(({ openReviews: _openReviews, ...rest }) => rest);

  const reviewerIds = pickReviewers(reviewers, reviewerCount, rules.requiredFocus, rules.requiredReviewers, byId);
  const missingRequiredFocus = rules.requiredFocus.some(
    (focus) => !reviewerIds.some((id) => byId.get(id)?.focus.includes(focus)),
  );

  const firedRules = rules.fired.map((r) => ({ id: r.id, name: r.name, effect: r.effect, value: r.value }));
  // buildCodebaseMap keeps hotFiles sorted by commits and capped; the slice only guards hand-built maps.
  const hotFiles = (ctx.map?.hotFiles ?? [])
    .slice(0, HOT_FILE_LIMIT)
    .map((h) => h.path)
    .filter((path) => task.areas.some((area) => matchGlob(area, path) || matchGlob(dirGlob(area), path)));
  const confidence = computeConfidence(builders, builder ?? null, reviewerIds.length < reviewerCount);
  const explanation = explain({
    mode,
    builder: builder ?? null,
    builderActor,
    reviewers: reviewerIds.map((id) => reviewers.find((r) => r.actorId === id)!).filter(Boolean),
    reviewerShortfall: reviewerCount - reviewerIds.length,
    missingRequiredFocus,
    risk,
    skills,
    firedRules: firedRules.map((r) => r.name),
    learned,
    hotFiles,
  });

  return {
    mode,
    builderId: builder?.actorId ?? null,
    reviewerIds,
    skills,
    risk,
    firedRules,
    // Top candidates of each kind, so the human sees the agent and the people it was weighed against.
    builders: [...builders.filter((b) => b.kind === "agent").slice(0, 3), ...builders.filter((b) => b.kind === "human").slice(0, 3)].sort(
      (a, b) => b.score - a.score || a.name.localeCompare(b.name),
    ),
    reviewers: reviewers.slice(0, 3),
    disqualified,
    explanation,
    confidence,
    learned,
  };
}

function pickReviewers(
  ranked: ReviewerScore[],
  count: number,
  requiredFocus: string[],
  requiredReviewers: string[],
  byId: Map<string, Actor>,
): string[] {
  if (count === 0) return [];
  const picked: string[] = [];
  for (const id of requiredReviewers) {
    if (ranked.some((r) => r.actorId === id) && !picked.includes(id)) picked.push(id);
  }
  for (const focus of requiredFocus) {
    if (picked.some((id) => byId.get(id)?.focus.includes(focus))) continue;
    const withFocus = ranked.find((r) => byId.get(r.actorId)?.focus.includes(focus) && !picked.includes(r.actorId));
    if (withFocus) picked.push(withFocus.actorId);
  }
  for (const r of ranked) {
    if (picked.length >= count) break;
    if (!picked.includes(r.actorId)) picked.push(r.actorId);
  }
  return picked;
}

export function computeConfidence(builders: readonly ScoreBreakdown[], builder: ScoreBreakdown | null, reviewerShortfall: boolean): number {
  if (!builder) return 0;
  const runnerUp = builders.find((b) => b.actorId !== builder.actorId && b.kind === builder.kind);
  const margin = runnerUp ? builder.score - runnerUp.score : 0.3;
  let c = 0.5 + builder.skillMatch * 0.3 + Math.min(0.2, margin);
  if (reviewerShortfall) c -= 0.3;
  return round(Math.max(0, Math.min(1, c)));
}

/** The §4.4 clause naming why history favoured the builder; empty when it did not matter. */
function learnedClause(l: LearnedRouting | null): string {
  if (!l) return "";
  const similar = l.basis === "areas" ? "similar tasks on these areas" : "similar tasks";
  const reverted = l.reverted > 0 ? `${l.reverted} reverted` : "";
  const record = `${l.landed}/${l.attempts}`;
  if (l.flipped && l.over) {
    // A builder with no track of its own was lifted only because the displaced agent's record dragged it down.
    if (l.attempts === 0) return `; chosen over ${l.over.name} whose record on similar tasks is ${l.over.landed}/${l.over.attempts}`;
    const vs = l.over.attempts > 0 ? `${l.over.landed}/${l.over.attempts}` : "no record";
    return `; chosen over ${l.over.name} because it merged ${record} ${similar}${reverted ? ` (${reverted})` : ""} vs ${vs}`;
  }
  if (l.adjustment > 0) return `; chosen because it merged ${record} ${similar} (${l.firstTry} first try${reverted ? `, ${reverted}` : ""})`;
  return `; track on ${similar}: merged ${record}${reverted ? ` (${reverted})` : ""}`;
}

function explain(input: {
  mode: Mode;
  builder: ScoreBreakdown | null;
  builderActor: Actor | undefined;
  reviewers: ReviewerScore[];
  reviewerShortfall: number;
  missingRequiredFocus: boolean;
  risk: RiskAssessment;
  skills: string[];
  firedRules: string[];
  learned: LearnedRouting | null;
  hotFiles: string[];
}): string {
  const parts: string[] = [];
  const skills = input.skills.length ? input.skills.join(", ") : "no specific skills";
  parts.push(`Risk ${input.risk.tier} (${input.risk.reasons.join("; ")}). Needs ${skills}.`);
  if (!input.builder) {
    parts.push("No eligible builder right now.");
  } else {
    const who = input.builder.kind === "agent" ? `agent ${input.builder.name}` : input.builder.name;
    const modeText =
      input.mode === "agent"
        ? "Agent builds; automated evidence and agent review are enough at this risk"
        : input.mode === "agent_with_review"
          ? "Agent builds, a human reviews"
          : "A human builds (agents may assist)";
    const availability = input.builder.kind === "agent" ? `, availability ${Math.round((input.builder.availability ?? 0) * 100)}%` : "";
    parts.push(
      `${modeText}. Builder: ${who} — skill match ${Math.round(input.builder.skillMatch * 100)}%${availability}${
        input.builder.continuity ? ", worked on these areas before" : ""
      }${learnedClause(input.learned)}${
        input.builder.missingSkills.length ? `; missing ${input.builder.missingSkills.join(", ")}` : ""
      }.`,
    );
  }
  for (const r of input.reviewers) {
    parts.push(
      `Reviewer: ${r.name} — skill match ${Math.round(r.skillMatch * 100)}%${r.focusMatch ? ", has the required focus" : ""}.`,
    );
  }
  if (input.reviewerShortfall > 0) {
    const cause = input.missingRequiredFocus ? "nobody eligible has the required focus" : "not enough eligible human reviewers";
    parts.push(`Missing ${input.reviewerShortfall} reviewer(s): ${cause}.`);
  }
  if (input.firedRules.length) parts.push(`Rules: ${input.firedRules.join("; ")}.`);
  for (const path of input.hotFiles) parts.push(`Also touches hot file ${path}.`);
  return parts.join(" ");
}

// ───────────────── routing by machine-connected capability (docs/specs/machine-connected-services.md §7) ─────────────────

/** A capability counts only if its node checked it this recently (spec §7 "State freshness"); an older report reads as unknown. */
export const SERVICE_FRESH_MS = 15 * 60_000;

/** The state a catalog probe reports for a signed-in service ("ready" in the spec's words). */
const READY_STATE = "connected";

/** A task's declared capabilities: `name` or `name:scope`, lower case, from the catalog. Unknown names are dropped, duplicates collapsed. */
export function normalizeRequiredServices(raw: readonly string[] | null | undefined): string[] {
  const out: string[] = [];
  for (const entry of raw ?? []) {
    const cap = entry.trim().toLowerCase();
    const [name, scope, ...rest] = cap.split(":");
    const svc = name ? serviceCapability(name) : null;
    // A scope must be one of the service's catalog hosts, or the task could never be satisfied and would wait forever.
    if (rest.length > 0 || !svc || scope === "" || (scope !== undefined && !(svc.hosts as readonly string[]).includes(scope))) continue;
    if (!out.includes(cap)) out.push(cap);
  }
  return out;
}

/** The host a GitHub repo URL lives on (`https://github.com/o/r`, `git@github.com:o/r.git`), or null when it is not a github.com URL. */
export function githubHostOf(repoUrl: string | null | undefined): string | null {
  return repoUrl && /(^|[/@])github\.com[/:]/i.test(repoUrl) ? "github.com" : null;
}

/**
 * Everything a task needs a machine to be signed in to: what its author declared, plus `github:<host>` derived for a
 * project whose delivery uses `gh` (`push_mode = pr`). Direct and branch pushes need only git, so they derive nothing.
 */
export function requiredCapabilities(task: { requiresServices?: readonly string[] | null; deliverable?: string | null }, project: { repoUrl?: string | null; pushMode?: string | null; github?: { bound?: boolean } | null } | null): string[] {
  const declared = normalizeRequiredServices(task.requiresServices);
  // Only code work publishes through gh; artifact, check and action tasks never open a PR.
  // A project bound to the GitHub App publishes with hub-minted tokens, so no machine needs a gh login.
  const host = project?.pushMode === "pr" && !project.github?.bound && (task.deliverable ?? "code") === "code" ? githubHostOf(project.repoUrl) : null;
  if (host && !declared.some((c) => c === "github" || c.startsWith("github:"))) declared.push(`github:${host}`);
  return declared;
}

type NodeServicesView = { online: boolean; services?: readonly { name: string; scope: string; state: string; checkedAt: number }[] };

/** True when the node is live and reported `capability` as signed in within SERVICE_FRESH_MS. A bare `name` matches any scope. */
export function nodeHasCapability(node: NodeServicesView, capability: string, now: number): boolean {
  if (!node.online) return false;
  const [name, scope] = capability.split(":");
  return (node.services ?? []).some((s) => s.name === name && (scope === undefined || s.scope === scope) && s.state === READY_STATE && now - s.checkedAt <= SERVICE_FRESH_MS);
}

const SERVICE_TITLES: Record<string, string> = { github: "GitHub", vercel: "Vercel", supabase: "Supabase", fly: "Fly.io", gcloud: "Google Cloud" };

/**
 * Why a task cannot be handed out yet, or null when some node in `candidates` has every required capability ready and fresh.
 * Names what is missing on the node closest to qualifying: "waiting for a machine logged in to Vercel". The task is never
 * failed and never goes to a node that does not qualify; it stays ready and this is the reason shown.
 */
export function serviceWaitReason(required: readonly string[], candidates: readonly NodeServicesView[], now: number): string | null {
  if (required.length === 0) return null;
  if (candidates.some((n) => required.every((c) => nodeHasCapability(n, c, now)))) return null;
  let best: string[] = [...required];
  for (const n of candidates) {
    const missing = required.filter((c) => !nodeHasCapability(n, c, now));
    if (missing.length < best.length) best = missing;
  }
  const names = [...new Set(best.map((c) => SERVICE_TITLES[c.split(":")[0]!] ?? c))];
  return `waiting for a machine logged in to ${names.join(" and ")}`;
}
